import { QuestionMark } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getPermissions, getRoles } from 'shared/api/admin';
import { Title } from 'shared/components/abstract/interactables';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { useRequest } from 'shared/hooks';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';

function PermissionHelp() {
  const [show, setShow] = useState(false);

  return (
    <>

      <Tooltip title="Learn more about Access Control">
        <IconButton
          color={BUTTON_COLORS.primary}
          onClick={() => setShow(true)}
        >
          <QuestionMark />
        </IconButton>
      </Tooltip>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        size="lg"
      >
        <DialogTitle>Roles and Permissions</DialogTitle>
        <DialogContent dividers>
          <p>
            Access control is achieved by assigning roles to users.
            The
            <em> admin </em>
            role grants access to everything.
          </p>
          <p>
            A role grants access to many different resources.
            A resource is an API route, for example
            <code> POST /api/v1/coupon </code>
            is a resource for creating coupons.
            <code> POST </code>
            is a HTTP method that is reserved for routes that create resources. Similarly,
            <code> DELETE </code>
            is for deletions,
            <code> PUT </code>
            is for updates, and
            <code> GET </code>
            is for reading.
          </p>
          <p>
            It would be painful to enumerate every route that is available for every role.
            To simplify we use route prefixes to determine if a role is allowed access.
          </p>
          <p>
            If a role had
            <code> /api/v1/voucher, GET </code>
            it would allow users to view the following resources:
          </p>
          <pre>
            <code>/api/v1/voucher/batch?</code>
            <br />
            <code>/api/v1/voucher/batch/1</code>
            <br />
            <code>/api/v1/voucher/reports/manager?</code>
            <br />
            <code>etc.</code>
          </pre>
          <p>
            A role is made of route prefix and method pairings.
            When a user makes a resource request the server checks each row in this table.
            Access is granted when the API route matches both a prefix and method.
          </p>
          <p>
            We use conventions to make setting methods concise.
            <code> ANY </code>
            allows any method. Methods may also be combined to match multiple.
          </p>
          <p>
            If we wanted a role to be able to view or update vouchers:
            <code> /api/v1/voucher, GET|PUT </code>
            If we instead wanted them to access every resource under vouchers:
            <code> /api/v1/voucher, ANY </code>
          </p>
          <p>
            If a user is denied access to a resource a
            <em> Permission Denied </em>
            error will be displayed.
            However, this should not happen often. This application attempts to only show links
            that the role is allowed to access.
          </p>
          <p>
            Roles and their permissions are not editable from within the application.
            Instead developers may edit them when the need arises.
            This is a design choice for a few reasons:
          </p>
          <ul>
            <li>Permission changes are tested and reviewed</li>
            <li>System simplicity</li>
            <li>Roles should not change often; adding dynamic roles would create more problems</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button variant={VARIANTS.contained} onClick={() => setShow(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function PermissionViewer() {
  const { request } = useRequest();
  const [role, setRole] = useState(undefined);
  const [permissions, setPermissions] = useState([]);
  const isMobile = useMediaQuery('(max-width: 300px)');

  const columns = [
    {
      name: 'Route Prefix',
      jsonKey: 'RoutePrefix',
    },
    {
      name: 'Method',
      jsonKey: 'Method',
    },
  ];

  useEffect(() => {
    (async () => {
      if (!role?.name) {
        setPermissions([]);
        return;
      }
      if (role.name === 'admin') {
        setPermissions([{
          RoutePrefix: 'All',
          Method: 'All',
        }]);
        return;
      }
      const permissionResponse = await request(getPermissions(role.name));
      if (permissionResponse) {
        setPermissions(permissionResponse);
      }
    })();
  }, [role, request]);

  return (
    <>
      <Title title="Access Control Settings" />
      <Card
        sx={{
          maxWidth: '400px',
          marginBottom: 2,
          background: 'subtleBackground',
        }}
      >
        <CardContent>
          <Box sx={{
            display: isMobile ? 'flex flex-col' : 'flex',
            alignItems: 'center',
            flexWrap: true,
            gap: 1,
          }}
          >
            <Box sx={{ width: '100%' }}>
              <AsyncSearchableSelect
                label="Role"
                onChange={setRole}
                searchRequest={getRoles}
                getOptionValue={(r) => r.id}
                getOptionLabel={(r) => r.name}
                selectedOption={role}
              />
            </Box>
            <PermissionHelp />
          </Box>
        </CardContent>
      </Card>
      <StickyHeadTable
        columns={columns}
        rows={permissions}
        rowId="RoutePrefix"
        noRowsText="No Permissions"
        striped
        className="table-height-large"
      />
    </>
  );
}
